// src/services/payment-service.js - EXPANDED VERSION
// Gradually adding back functionality
import firestoreService from './firestore-service';

const USE_STRIPE = true; // false = localStorage, true = Stripe
const BACKEND_URL = 'http://localhost:5001';

class PaymentService {
  constructor() {
    this.mode = USE_STRIPE ? 'stripe' : 'localStorage';
  }

  // Process one-time payment
  async processPayment(product) {
    if (this.mode === 'localStorage') {
      // Simulate async
      await this.delay(500);
      
      const purchase = {
        id: `purchase_${Date.now()}`,
        productName: product.name,
        amount: product.price,
        status: 'completed',
        date: new Date().toISOString(),
        mode: 'test'
      };
      
      // Save to localStorage
      const purchases = this.getPurchaseHistory();
      purchases.push(purchase);
      localStorage.setItem('userPurchases', JSON.stringify(purchases));
      
      return {
        success: true,
        purchase,
        message: 'Payment processed (test mode)'
      };
    } else {
      // Stripe mode - redirect to checkout
      try {
        const response = await fetch(`${BACKEND_URL}/create-checkout-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productName: product.name,
            priceInCents: product.price
          })
        });
        
        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
          return { success: true, redirecting: true };
        }
        throw new Error(data.error || 'Failed to create session');
      } catch (error) {
        return { success: false, error: error.message };
      }
    }
  }

  // Create subscription
  async createSubscription(plan) {
    if (this.mode === 'localStorage') {
      await this.delay(500);
      
      const subscription = {
        id: `sub_${Date.now()}`,
        planName: plan.name,
        amount: plan.price,
        interval: plan.interval || 'month',
        status: 'active',
        type: 'subscription',
        startedAt: new Date().toISOString(),
        mode: 'test'
      };
      
      const purchases = this.getPurchaseHistory();
      purchases.push(subscription);
      localStorage.setItem('userPurchases', JSON.stringify(purchases));
      
      return {
        success: true,
        subscription,
        message: 'Subscription created (test mode)'
      };
    } else {
      // Stripe subscription logic here
      return { success: false, message: 'Stripe subscriptions coming soon' };
    }
  }

  getPurchaseHistory() {
    return JSON.parse(localStorage.getItem('userPurchases') || '[]');
  }

  clearTestPurchases() {
    if (this.mode === 'localStorage') {
      localStorage.removeItem('userPurchases');
      return { success: true };
    }
    return { success: false, message: 'Only works in test mode' };
  }

  formatPrice(cents) {
    return `${(cents / 100).toFixed(2)}`;
  }

  isTestMode() {
    return this.mode === 'localStorage';
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // ==================== STRIPE CONNECT METHODS ====================

  /**
   * Create Connect Account & Get Onboarding URL
   * @param {string} userId - User ID
   * @param {string} email - Practitioner email
   * @param {string} businessName - Business name
   * @returns {Promise<{success: boolean, accountId?: string, onboardingUrl?: string}>}
   */
  async createConnectAccount(userId, email, businessName) {
    if (this.mode === 'localStorage') {
      // Mock implementation
      await this.delay(1500);

      const mockAccountId = `acct_mock_${Date.now()}`;

      // Store in localStorage
      const connectData = {
        accountId: mockAccountId,
        email: email,
        businessName: businessName,
        connected: true,
        chargesEnabled: true,
        detailsSubmitted: true,
        connectedAt: new Date().toISOString()
      };

      localStorage.setItem('practitioner_stripe_connect', JSON.stringify(connectData));

      // Background sync to Firestore
      const userId = firestoreService.getCurrentUserId();
      if (userId) {
        firestoreService.saveStripeConnect(userId, connectData).then(() => {
        }).catch(err => {
          console.warn('⚠️ Firestore stripe connect save failed:', err);
        });
      }

      return {
        success: true,
        accountId: mockAccountId,
        onboardingUrl: `/wizard/payment-success?accountId=${mockAccountId}` // Mock redirect
      };
    } else {
      // Real Stripe mode
      try {
        const response = await fetch(`${BACKEND_URL}/create-connect-account`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, email, businessName })
        });

        const data = await response.json();

        if (data.success && data.onboardingUrl) {
          // Redirect to Stripe hosted onboarding
          window.location.href = data.onboardingUrl;
          return { success: true, redirecting: true, accountId: data.accountId };
        }

        throw new Error(data.error || 'Failed to create connect account');
      } catch (error) {
        console.error('❌ payment-service: Error:', error);
        return { success: false, error: error.message };
      }
    }
  }

  /**
   * Get Stripe Connect Account Status
   * @param {string} accountId - Stripe account ID
   * @returns {Promise<{success: boolean, isActive?: boolean, chargesEnabled?: boolean}>}
   */
  async getConnectAccountStatus(accountId) {
    if (this.mode === 'localStorage') {
      await this.delay(300);

      let connectData = localStorage.getItem('practitioner_stripe_connect');

      // If localStorage is empty, try Firestore
      if (!connectData) {
        const userId = firestoreService.getCurrentUserId();
        if (userId) {
          try {
            const firestoreData = await firestoreService.getStripeConnect(userId);
            if (firestoreData && Object.keys(firestoreData).length > 0) {
              localStorage.setItem('practitioner_stripe_connect', JSON.stringify(firestoreData));
              connectData = JSON.stringify(firestoreData);
            }
          } catch (err) {
            console.warn('Firestore stripe connect read failed:', err);
          }
        }
      }

      if (connectData) {
        const data = JSON.parse(connectData);
        return {
          success: true,
          isActive: data.connected,
          chargesEnabled: data.chargesEnabled,
          payoutsEnabled: true,
          detailsSubmitted: data.detailsSubmitted
        };
      }

      return {
        success: false,
        isActive: false,
        chargesEnabled: false
      };
    } else {
      // Real Stripe mode
      try {
        const response = await fetch(`${BACKEND_URL}/connect-account-status/${accountId}`);
        const data = await response.json();
        return data;
      } catch (error) {
        return { success: false, error: error.message };
      }
    }
  }

  /**
   * Reconnect/Update Stripe Account
   * @param {string} accountId - Stripe account ID
   * @returns {Promise<{success: boolean, onboardingUrl?: string}>}
   */
  async reconnectAccount(accountId) {
    if (this.mode === 'localStorage') {
      await this.delay(1000);

      // In test mode, just return success
      return {
        success: true,
        onboardingUrl: `/dashboard/settings/business?reconnected=true`
      };
    } else {
      // Real Stripe mode
      try {
        const response = await fetch(`${BACKEND_URL}/reconnect-account`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accountId })
        });

        const data = await response.json();

        if (data.success && data.onboardingUrl) {
          // Redirect to Stripe hosted update
          window.location.href = data.onboardingUrl;
          return { success: true, redirecting: true };
        }

        throw new Error(data.error || 'Failed to reconnect account');
      } catch (error) {
        return { success: false, error: error.message };
      }
    }
  }

  /**
   * Get current Stripe Connect status from localStorage
   * @returns {Promise<object|null>} Connect data or null
   */
  async getStoredConnectStatus() {
    let data = localStorage.getItem('practitioner_stripe_connect');

    // If localStorage is empty, try Firestore
    if (!data) {
      const userId = firestoreService.getCurrentUserId();
      if (userId) {
        try {
          const firestoreData = await firestoreService.getStripeConnect(userId);
          if (firestoreData && Object.keys(firestoreData).length > 0) {
            localStorage.setItem('practitioner_stripe_connect', JSON.stringify(firestoreData));
            data = JSON.stringify(firestoreData);
          }
        } catch (err) {
          console.warn('Firestore stripe connect read failed:', err);
        }
      }
    }

    return data ? JSON.parse(data) : null;
  }

  /**
   * Clear Stripe Connect data (disconnect)
   * @returns {Promise<{success: boolean}>}
   */
  async disconnectStripeConnect() {
    if (this.mode === 'localStorage') {
      await this.delay(500);
      localStorage.removeItem('practitioner_stripe_connect');

      // Background sync: remove from Firestore too
      const userId = firestoreService.getCurrentUserId();
      if (userId) {
        firestoreService.saveStripeConnect(userId, null).then(() => {
        }).catch(err => {
          console.warn('⚠️ Firestore stripe connect removal failed:', err);
        });
      }

      return { success: true };
    } else {
      // Real mode: would call backend to revoke tokens
      localStorage.removeItem('practitioner_stripe_connect');

      // Background sync: remove from Firestore too
      const userId = firestoreService.getCurrentUserId();
      if (userId) {
        firestoreService.saveStripeConnect(userId, null).catch(err => {
          console.warn('⚠️ Firestore stripe connect removal failed:', err);
        });
      }

      return { success: true };
    }
  }
}

// Create and export instance
const paymentService = new PaymentService();
export default paymentService;